import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MapPin, ChevronRight, FileText, Sparkles, Image, Info, Leaf, Shield } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  useAdminPlace, 
  useCreateAdminPlace, 
  useUpdateAdminPlace 
} from "@/hooks/useAdminPlaces";
import { useMediaAssets } from "@/hooks/useMediaAssets";
import { REGIONS } from "@/features/producer-editor/types";
import { Skeleton } from "@/components/ui/skeleton";
import { PlaceAddressAutocomplete } from "@/components/admin/PlaceAddressAutocomplete";
import { PlaceImagesSection } from "@/components/admin/PlaceImagesSection";
import { PlaceTagsEditor } from "@/components/admin/PlaceTagsEditor";
import { PlaceSummarySection } from "@/components/editorial/PlaceSummarySection";

import { PlaceCoreEditorCard } from "@/components/admin/place-core/PlaceCoreEditorCard";
import { placeCoreSchema, EMPTY_PLACE_CORE, migratePlaceCore } from "@/components/admin/place-core/placeCoreSchema";
import { PlaceEditorTopBar } from "@/components/admin/PlaceEditorTopBar";
import { computeDirty, type PlaceDraft } from "@/lib/admin/dirty";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { useDraftSessionId, clearDraftSessionId } from "@/hooks/useDraftSessionId";
import { finalizeDraftSession } from "@/lib/media/mediaApi";

import { cn } from "@/lib/utils";
import { PlaceSeasonCards } from "@/components/place/editor/PlaceSeasonCards";
import { useSeasonOverlays } from "@/hooks/useSeasonOverlays";
import { StatusPanel } from "@/components/admin/place-editor/StatusPanel";
import { SammanhangsCard } from "@/components/admin/place-editor/SammanhangsCard";
import { SectionGroupHeading } from "@/components/admin/place-editor/SectionGroupHeading";
// ─── Schema ──────────────────────────────────────────────────────

const placeSchema = z.object({
  name: z.string().min(2, "Namn måste vara minst 2 tecken"),
  slug: z.string().min(2, "Slug måste vara minst 2 tecken"),
  municipality: z.string().optional(),
  landskap: z.string().optional(),
  county: z.string().optional(),
  address_text: z.string().optional(),
  description_short: z.string().optional(),
  website: z.string().optional(),
  instagram: z.string().optional(),
  phone: z.string().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  status: z.enum(["draft", "public"]),
  tags: z.array(z.string()).default([]),
  place_core: placeCoreSchema.default(EMPTY_PLACE_CORE),
});

type PlaceFormData = z.infer<typeof placeSchema>;

// ─── Section types ───────────────────────────────────────────────

type SectionKey = 'basic' | 'placecore' | 'images' | 'practical' | 'seasons';

// ─── Status helpers ──────────────────────────────────────────────

type SectionStatus = 'saknas' | 'påbörjad' | 'klar';

function getBasicStatus(v: PlaceFormData): SectionStatus {
  const hasName = v.name.trim().length >= 2;
  const hasSlug = v.slug.trim().length >= 2;
  const hasSummary = (v.description_short || '').trim().length >= 40;
  if (!hasName && !hasSlug) return 'saknas';
  if (hasName && hasSlug && hasSummary) return 'klar';
  return 'påbörjad';
}

function getPlaceCoreStatus(v: PlaceFormData): SectionStatus {
  const vibeLen = (v.place_core?.vibe || '').trim().length;
  const intentCount = (v.place_core?.intents || []).length;
  if (vibeLen === 0 && intentCount === 0) return 'saknas';
  if (vibeLen >= 80 && intentCount >= 1) return 'klar';
  return 'påbörjad';
}

function getImagesStatus(coverCount: number, galleryCount: number): SectionStatus {
  const total = coverCount + galleryCount;
  if (total === 0) return 'saknas';
  return 'klar';
}

function getPracticalStatus(v: PlaceFormData): SectionStatus {
  const hasLocation = !!(v.municipality || v.landskap || v.address_text);
  const hasCoords = v.lat != null && v.lng != null;
  const hasContact = !!(v.website || v.instagram || v.phone);
  if (!hasLocation && !hasCoords && !hasContact) return 'saknas';
  if (hasLocation) return 'klar';
  return 'påbörjad';
}

function StatusPill({ status }: { status: SectionStatus }) {
  const styles: Record<SectionStatus, string> = {
    saknas: 'bg-destructive/10 text-destructive',
    påbörjad: 'bg-accent text-accent-foreground',
    klar: 'bg-primary/10 text-primary',
  };
  const labels: Record<SectionStatus, string> = {
    saknas: 'Saknas',
    påbörjad: 'Påbörjad',
    klar: 'Klar',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-[11px] font-medium', styles[status])}>
      {labels[status]}
    </span>
  );
}

// ─── Accordion Row ───────────────────────────────────────────────

interface AccordionRowProps {
  icon: React.ReactNode;
  title: string;
  summary: string;
  status?: SectionStatus;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  sectionRef: React.RefObject<HTMLDivElement | null>;
}

function AccordionRow({ icon, title, summary, status, isOpen, onToggle, children, sectionRef }: AccordionRowProps) {
  return (
    <div ref={sectionRef} className="rounded-lg border bg-card text-card-foreground scroll-mt-24">
      {/* Collapsed header row */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/40',
          isOpen && 'border-b'
        )}
      >
        <span className="text-muted-foreground shrink-0">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{title}</span>
            {status && <StatusPill status={status} />}
          </div>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{summary}</p>
        </div>
        <ChevronRight className={cn(
          'h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0',
          isOpen && 'rotate-90'
        )} />
      </button>

      {/* Expanded content */}
      {isOpen && (
        <div className="px-4 py-5" onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────

export default function AdminPlaceEditPage() {
  const navigate = useNavigate();
  const { placeId } = useParams<{ placeId: string }>();
  const { pathname } = useLocation();
  const isNew = pathname.endsWith("/admin/places/new");
  const queryClient = useQueryClient();

  const { data: place, isLoading } = useAdminPlace(isNew ? undefined : placeId);
  const createMutation = useCreateAdminPlace();
  const updateMutation = useUpdateAdminPlace();

  // Media counts for status
  const { data: coverAssets = [] } = useMediaAssets('place', placeId || null, 'cover', null);
  const { data: galleryAssets = [] } = useMediaAssets('place', placeId || null, 'gallery', null);
  const { data: seasonOverlays = [] } = useSeasonOverlays(placeId);
  const seasonsFilled = seasonOverlays.filter(
    (o) => !!(o.summary_sv || o.hero_media_asset_id || (o.gallery_media_asset_ids?.length ?? 0) > 0)
  ).length;

  const draftKey = `place-editor-${placeId ?? 'new'}`;
  const draftSessionId = useDraftSessionId(draftKey);

  const [addressSearchValue, setAddressSearchValue] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [openSection, setOpenSection] = useState<SectionKey | null>(null);
  const justSavedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialDraftRef = useRef<PlaceDraft | null>(null);

  // Section refs for scroll
  const basicRef = useRef<HTMLDivElement>(null);
  const placecoreRef = useRef<HTMLDivElement>(null);
  const imagesRef = useRef<HTMLDivElement>(null);
  const practicalRef = useRef<HTMLDivElement>(null);
  const seasonsRef = useRef<HTMLDivElement>(null);
  const sectionRefs: Record<SectionKey, React.RefObject<HTMLDivElement | null>> = {
    basic: basicRef,
    placecore: placecoreRef,
    images: imagesRef,
    practical: practicalRef,
    seasons: seasonsRef,
  };

  const form = useForm<PlaceFormData>({
    resolver: zodResolver(placeSchema),
    defaultValues: {
      name: "", slug: "", municipality: "", landskap: "", county: "",
      address_text: "", description_short: "", website: "", instagram: "",
      phone: "", lat: null, lng: null, status: "draft", tags: [],
      place_core: EMPTY_PLACE_CORE,
    },
  });

  const currentValues = form.watch();
  const dirty = initialDraftRef.current
    ? computeDirty(initialDraftRef.current, currentValues as PlaceDraft)
    : false;

  useEffect(() => {
    if (dirty && justSaved) setJustSaved(false);
  }, [dirty, justSaved]);

  const {
    isConfirmOpen, requestNavigate, confirmNavigation, cancelNavigation,
  } = useUnsavedChangesGuard({ isDirty: dirty });

  useEffect(() => {
    if (place) {
      const resetValues: PlaceFormData = {
        name: place.name, slug: place.slug,
        municipality: place.municipality || "", landskap: place.landskap || "",
        county: place.county || "", address_text: place.address_text || "",
        description_short: place.description_short || "",
        website: place.website || "", instagram: place.instagram || "",
        phone: place.phone || "", lat: place.lat, lng: place.lng,
        status: place.status, tags: place.tags || [],
        place_core: migratePlaceCore((place as any).place_core || {}),
      };
      form.reset(resetValues);
      initialDraftRef.current = resetValues as PlaceDraft;
      setAddressSearchValue(place.address_text || "");
    }
  }, [place, form]);


  const watchName = form.watch("name");
  useEffect(() => {
    if (isNew && watchName) {
      const slug = watchName
        .toLowerCase().replace(/[åä]/g, "a").replace(/ö/g, "o")
        .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      form.setValue("slug", slug);
    }
  }, [watchName, isNew, form]);

  const handleAddressSelect = (data: {
    address_text: string; municipality: string | null; county: string | null;
    landskap: string | null; lat: number; lng: number;
  }) => {
    form.setValue("address_text", data.address_text);
    form.setValue("municipality", data.municipality || "");
    form.setValue("county", data.county || "");
    if (data.landskap) form.setValue("landskap", data.landskap);
    form.setValue("lat", data.lat);
    form.setValue("lng", data.lng);
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  const onSubmit = useCallback(async (data: PlaceFormData) => {
    const payload = {
      name: data.name, slug: data.slug,
      municipality: data.municipality || null, landskap: data.landskap || null,
      county: data.county || null, address_text: data.address_text || null,
      description_short: data.description_short || null,
      website: data.website || null, instagram: data.instagram || null,
      phone: data.phone || null, lat: data.lat, lng: data.lng,
      status: data.status, tags: data.tags || [],
      place_core: data.place_core || EMPTY_PLACE_CORE,
      contact_phone: null as string | null,
      contact_website: null as string | null,
      contact_instagram: null as string | null,
    };
    try {
      if (isNew) {
        const created = await createMutation.mutateAsync(payload);
        try {
          await finalizeDraftSession(draftSessionId, 'place', created.id);
          clearDraftSessionId(draftKey);
          queryClient.invalidateQueries({ queryKey: ['media-assets', 'place', created.id] });
        } catch (e) { console.warn('[place-save] finalize failed:', e); }
        navigate(`/app/admin/places/${created.id}/edit`);
      } else if (placeId) {
        await updateMutation.mutateAsync({ id: placeId, updates: payload });
        initialDraftRef.current = data as PlaceDraft;
        setJustSaved(true);
        if (justSavedTimerRef.current) clearTimeout(justSavedTimerRef.current);
        justSavedTimerRef.current = setTimeout(() => setJustSaved(false), 3000);
      }
    } catch { /* Error toasts handled by mutations */ }
  }, [isNew, placeId, createMutation, updateMutation, navigate, draftSessionId, draftKey, queryClient]);

  useEffect(() => () => {
    if (justSavedTimerRef.current) clearTimeout(justSavedTimerRef.current);
  }, []);

  const goBack = useCallback(() => navigate("/app/admin/places"), [navigate]);

  // Toggle accordion
  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSection(prev => {
      const next = prev === key ? null : key;
      if (next) {
        setTimeout(() => {
          sectionRefs[next]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
      return next;
    });
  }, []);

  // ─── Compute summaries ──────────────────────────────────────────

  const v = currentValues;
  const descLen = (v.description_short || '').trim().length;
  const vibeLen = (v.place_core?.vibe || '').trim().length;
  const intentCount = (v.place_core?.intents || []).length;
  const totalImages = coverAssets.length + galleryAssets.length;
  const hasCover = coverAssets.length > 0;
  const locationParts = [v.municipality, v.landskap].filter(Boolean);
  const hasContact = !!(v.website || v.instagram || v.phone);
  const hasCoords = v.lat != null && v.lng != null;

  const basicSummary = v.name
    ? `${v.name} • ${v.slug} • ${descLen > 0 ? `${descLen} tecken sammanfattning` : 'Sammanfattning saknas'}`
    : 'Namn och slug saknas';

  const placeCoreSummary = vibeLen > 0 || intentCount > 0
    ? `${vibeLen}/600 tecken • ${intentCount} valda karaktärer`
    : 'Inga upplevelsefält ifyllda';

  const imagesSummary = totalImages > 0
    ? `${totalImages} bild${totalImages > 1 ? 'er' : ''} • Omslag: ${hasCover ? 'satt' : 'ej satt'}`
    : 'Inga bilder uppladdade';

  const practicalSummary = [
    locationParts.length > 0 ? locationParts.join('/') : null,
    `kontakt: ${hasContact ? 'ja' : 'nej'}`,
    `coords: ${hasCoords ? 'ja' : 'nej'}`,
  ].filter(Boolean).join(' • ');

  // ─── Render ─────────────────────────────────────────────────────

  if (!isNew && isLoading) {
    return (
      <AdminLayout title="Laddar...">
        <div className="space-y-4 max-w-2xl mx-auto">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isNew ? "Ny plats" : `Redigera ${place?.name || "plats"}`}>
      <PlaceEditorTopBar
        placeName={place?.name || form.watch("name") || ""}
        slug={form.watch("slug") || ""}
        dirty={dirty}
        saving={saving}
        justSaved={justSaved}
        isNew={isNew}
        status={form.watch("status") as "draft" | "public"}
        onStatusChange={(s) => form.setValue("status", s, { shouldDirty: true })}
        onSave={() => form.handleSubmit(onSubmit)()}
        onBack={goBack}
        confirmOpen={isConfirmOpen}
        onConfirmLeave={confirmNavigation}
        onCancelLeave={cancelNavigation}
        requestNavigate={requestNavigate}
      />

      <div className="h-3" />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="pb-16 md:pb-16" style={{ paddingBottom: 'max(4rem, calc(60px + env(safe-area-inset-bottom)))' }}>

          {/* ── 2-column editorial workspace (desktop) / single column (mobile) ── */}
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] max-w-6xl mx-auto">

            {/* ══ LEFT COLUMN: Content flow ══ */}
            <div className="space-y-2">

              {/* ── Group: Platsens identitet ── */}
              <SectionGroupHeading title="Platsens identitet" />

              {/* A) Grund */}
              <AccordionRow
                sectionRef={basicRef}
                icon={<FileText className="h-4 w-4" />}
                title="Grund"
                summary={basicSummary}
                status={getBasicStatus(v)}
                isOpen={openSection === 'basic'}
                onToggle={() => toggleSection('basic')}
              >
                <div id="section-grund" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormField control={form.control} name="name" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Namn *</FormLabel>
                        <FormControl><Input placeholder="Platsens namn" {...field} className="h-9" /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="slug" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Slug *</FormLabel>
                        <FormControl><Input placeholder="url-slug" {...field} className="h-9" /></FormControl>
                        <FormDescription className="text-[11px] text-muted-foreground/70">Används i URL:en</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <PlaceSummarySection
                    form={form}
                    placeName={currentValues.name?.trim() || ""}
                    municipality={currentValues.municipality}
                    landskap={currentValues.landskap}
                    tags={currentValues.tags}
                    seasons={currentValues.place_core?.seasons}
                    intentLabels={currentValues.place_core?.intents}
                    disabled={saving}
                  />
                </div>
              </AccordionRow>

              {/* B) PlaceCore */}
              <AccordionRow
                sectionRef={placecoreRef}
                icon={<Sparkles className="h-4 w-4" />}
                title="PlaceCore"
                summary={placeCoreSummary}
                status={undefined}
                isOpen={openSection === 'placecore'}
                onToggle={() => toggleSection('placecore')}
              >
                <div id="section-placecore">
                  <PlaceCoreEditorCard
                    data={form.watch("place_core")}
                    onChange={(val) => form.setValue("place_core", val, { shouldDirty: true })}
                    slug={form.watch("slug")}
                    placeId={placeId}
                    placeName={form.watch("name")}
                    municipality={form.watch("municipality")}
                    landskap={form.watch("landskap")}
                    tags={form.watch("tags")}
                    disabled={saving}
                  />
                </div>
              </AccordionRow>

              {/* ── Group: Så förändras den ── */}
              {!isNew && placeId && (
                <>
                  <SectionGroupHeading
                    title="Så förändras den"
                    subtitle="Kompletterar platsens grund beroende på säsong."
                    badge="Overlay-lager"
                  />

                  {/* C) Årstider */}
                  <AccordionRow
                    sectionRef={seasonsRef}
                    icon={<Leaf className="h-4 w-4" />}
                    title="Årstider"
                    summary={`${seasonsFilled} av 4 säsonger ifyllda`}
                    status={undefined}
                    isOpen={openSection === 'seasons'}
                    onToggle={() => toggleSection('seasons')}
                  >
                    <div id="section-arstider">
                      <PlaceSeasonCards placeId={placeId} />
                    </div>
                  </AccordionRow>
                </>
              )}
            </div>

            {/* ══ RIGHT COLUMN: Media, facts, status ══ */}
            <aside className="space-y-2 lg:sticky lg:top-24 lg:self-start">

              {/* ── Group: Visuellt & praktiskt ── */}
              <SectionGroupHeading title="Visuellt & praktiskt" />

              {/* D) Bilder */}
              <AccordionRow
                sectionRef={imagesRef}
                icon={<Image className="h-4 w-4" />}
                title="Bilder"
                summary={imagesSummary}
                status={getImagesStatus(coverAssets.length, galleryAssets.length)}
                isOpen={openSection === 'images'}
                onToggle={() => toggleSection('images')}
              >
                <div id="section-bilder">
                  <PlaceImagesSection
                    placeId={placeId}
                    draftSessionId={isNew ? draftSessionId : undefined}
                  />
                </div>
              </AccordionRow>

              {/* E) Praktisk information */}
              <AccordionRow
                sectionRef={practicalRef}
                icon={<MapPin className="h-4 w-4" />}
                title="Praktisk information"
                summary={practicalSummary}
                status={getPracticalStatus(v)}
                isOpen={openSection === 'practical'}
                onToggle={() => toggleSection('practical')}
              >
                <div id="section-praktiskt" className="space-y-5">
                  {/* Address search */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-foreground">Adress</p>
                    <PlaceAddressAutocomplete
                      addressValue={addressSearchValue}
                      onAddressChange={setAddressSearchValue}
                      onAddressSelect={handleAddressSelect}
                      lat={form.watch("lat")}
                      lng={form.watch("lng")}
                    />
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormField control={form.control} name="address_text" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Adress</FormLabel><FormControl><Input placeholder="Gatuadress" {...field} className="h-9" /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="municipality" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Kommun/Ort</FormLabel><FormControl><Input placeholder="Kommun" {...field} className="h-9" /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="landskap" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Landskap</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl><SelectTrigger className="h-9"><SelectValue placeholder="Välj landskap" /></SelectTrigger></FormControl>
                          <SelectContent className="bg-popover">
                            {REGIONS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="county" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Län</FormLabel><FormControl><Input placeholder="Län" {...field} className="h-9" /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormField control={form.control} name="lat" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Latitud</FormLabel>
                        <FormControl>
                          <Input type="number" step="any" placeholder="59.3293" {...field} className="h-9" value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="lng" render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Longitud</FormLabel>
                        <FormControl>
                          <Input type="number" step="any" placeholder="18.0686" {...field} className="h-9" value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <p className="text-xs font-medium text-foreground">Kontakt</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <FormField control={form.control} name="website" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">Webbplats</FormLabel><FormControl><Input placeholder="https://..." {...field} className="h-9" /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="instagram" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">Instagram</FormLabel><FormControl><Input placeholder="@användarnamn" {...field} className="h-9" /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="phone" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">Telefon</FormLabel><FormControl><Input placeholder="070-123 45 67" {...field} className="h-9" /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                  </div>

                  <Separator />

                  <PlaceTagsEditor
                    tags={form.watch("tags") || []}
                    onChange={(newTags) => form.setValue("tags", newTags, { shouldDirty: true })}
                    disabled={saving}
                    inline
                  />
                </div>
              </AccordionRow>

              {/* F) Status & nästa steg */}
              <StatusPanel
                placeName={v.name || ''}
                status={v.status as 'draft' | 'public'}
                descriptionLength={descLen}
                vibeLength={vibeLen}
                charTagCount={intentCount}
                totalImages={totalImages}
                hasCover={hasCover}
                hasLocation={locationParts.length > 0}
                hasCoords={hasCoords}
                hasContact={hasContact}
                seasonsFilled={seasonsFilled}
                isNew={isNew}
              />

              {/* G) Sammanhang – area context */}
              {!isNew && placeId && (
                <SammanhangsCard placeId={placeId} isAdmin={true} />
              )}
              {/* Link to ambassadors page */}
              {!isNew && placeId && (
                <Link
                  to={`/app/admin/places/${placeId}/editors`}
                  className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3 hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10 group-hover:bg-primary/15 transition-colors">
                    <Shield className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Ambassadörer</p>
                    <p className="text-[11px] text-muted-foreground">Hantera platsredaktörer</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              )}
            </aside>

          </div>
        </form>
      </Form>
    </AdminLayout>
  );
}
